import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { DOORWAYS } from "../../data/homeContent";

// 1.4 — What Brings You Here? The six locked Doorways.
// Per Task List §16 (decision locked): every card CTA opens the Starting Point
// selector with that Doorway pre-selected — no direct page navigation from cards.
export const SixDoorways = ({ onFind, light = false }) => {
  const [active, setActive] = useState(0);
  const headMain = light ? "text-[#1A1206]" : "text-cream";
  const headAccent = light ? "text-[#B07A2B]" : "text-gold";
  const subCls = light ? "text-[#3A2E1E]/75" : "text-cream/60";
  const ctaBtnCls = "group inline-flex items-center gap-2 btn-gold px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide w-fit";

  return (
    <section id="ways-to-enter" data-testid="ways-to-enter" className={`relative py-24 md:py-32 overflow-hidden ${light ? "" : "grain"}`}>
      {light ? (
        <>
          <div className="light-panel" />
          <div className="absolute top-1/3 -right-40 w-[42rem] h-[42rem] glow-bronze blur-3xl opacity-60 pointer-events-none" />
        </>
      ) : (
        <>
          <div className="line-weave" />
          <div className="aurora-weave slow" />
          <div className="absolute -top-24 right-0 w-[42rem] h-[42rem] radial-glow blur-3xl pointer-events-none" />
        </>
      )}
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <h2 className={`font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] ${headMain}`}>
            What Brings <span className={headAccent}>You Here?</span>
          </h2>
          <p className={`text-base md:text-lg ${subCls} max-w-xs md:text-right`}>
            Six doorways. One ecosystem. Begin wherever you are.
          </p>
        </div>

        {/* Desktop: expanding panels */}
        <div className="hidden md:flex gap-2.5 h-[32rem]">
          {DOORWAYS.map((g, i) => {
            const isActive = active === i;
            return (
              <motion.div
                key={g.id}
                data-testid={`doorway-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                className="relative rounded-2xl overflow-hidden cursor-pointer"
                animate={{ flexGrow: isActive ? 6 : 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ flexBasis: 0 }}
              >
                <img loading="lazy" src={g.img} alt={g.title} className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${isActive ? "scale-105" : "scale-100"}`} />
                <div className={`absolute inset-0 transition-all duration-500 ${isActive ? "bg-gradient-to-r from-[#080B12] via-[#080B12]/55 to-transparent" : "bg-[#080B12]/75"}`} />
                <div className="absolute top-5 left-5 flex items-center gap-2.5">
                  <span className="font-display text-2xl text-[#E7CC86]/90">{g.n}</span>
                  {/* §14 — each doorway carries its symbol from the shared icon family. */}
                  <g.icon size={20} strokeWidth={1.5} className="text-[#E7CC86]/80" aria-hidden="true" />
                </div>

                {!isActive && (
                  <div className="absolute inset-0 flex items-end p-4">
                    <p className="font-poppins font-semibold text-lg lg:text-xl text-cream [writing-mode:vertical-rl] rotate-180 tracking-wide whitespace-nowrap">{g.title}</p>
                  </div>
                )}

                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }}
                    className="absolute inset-0 flex flex-col justify-between p-8 pt-16"
                  >
                    <div>
                      <p className="font-serif italic text-[#E7CC86] text-lg max-w-md mb-2">“{g.intent}”</p>
                      <h3 className="font-display font-semibold text-3xl lg:text-4xl text-cream max-w-md mb-3">{g.title}</h3>
                      <p className="text-cream/75 text-base max-w-[300px]">{g.body}</p>
                    </div>
                    <div>
                      <button data-testid={`doorway-cta-${i}`} onClick={(e) => { e.stopPropagation(); onFind?.(g.id); }} className={ctaBtnCls}>
                        {g.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Mobile: stacked cards */}
        <div className="md:hidden space-y-4">
          {DOORWAYS.map((g, i) => (
            <div key={g.id} data-testid={`doorway-m-${i}`} className="relative rounded-2xl overflow-hidden h-auto min-h-[15rem]">
              <img loading="lazy" src={g.img} alt={g.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080B12] via-[#080B12]/55 to-transparent" />
              <div className="relative flex flex-col justify-end p-6">
                <span className="flex items-center gap-2 font-display text-xl text-[#E7CC86]/90">
                  {g.n}
                  <g.icon size={18} strokeWidth={1.5} className="text-[#E7CC86]/80" aria-hidden="true" />
                </span>
                <h3 className="font-display font-semibold text-2xl text-cream">{g.title}</h3>
                <p className="font-serif italic text-[#E7CC86] text-sm mt-1">“{g.intent}”</p>
                <p className="text-cream/70 text-sm mt-2 mb-4">{g.body}</p>
                <button data-testid={`doorway-m-cta-${i}`} onClick={() => onFind?.(g.id)} className={ctaBtnCls}>
                  {g.cta} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
